import axios from 'axios';
import type { AxiosInstance, AxiosInterceptorManager, AxiosResponse, AxiosRequestConfig } from 'axios';
import { apiErrorHandler } from '@/api/error-handler';
import { ReservationService } from '@/api/services/reservation';
import { CustomerService } from '@/api/services/customer';
import { NotificationService } from '@/api/services/notification';
import { getApiHost } from '@/services/config';
import { getAuthTokenFromStorage } from '@/services/utils';
import { ProductService } from '@/api/services/product';
import { PropertyService } from '@/api/services/property';
import { ServiceService } from './services/service';
import { PropertyConfigService } from './services/property-config';
import { PropertyKeysService } from '@/api/services/property-keys'
import { IdVerificationsService } from '@/api/services/id-verifications';

const requestAuthInterceptor = (config: AxiosRequestConfig) => {
  if (typeof config.headers !== 'undefined'
    && !config.headers['x-api-key']) {
    const authToken = getAuthTokenFromStorage();
    config.headers['Authorization'] = `Bearer ${authToken}`;
  }

  return config;
}

class ApiClient {
  private client: AxiosInstance;

  responseInterceptor: AxiosInterceptorManager<AxiosResponse>;
  reservation: ReservationService;
  customer: CustomerService;
  notification: NotificationService;
  product: ProductService;
  property: PropertyService;
  propertyKeys: PropertyKeysService;
  service: ServiceService;
  propertyConfig: PropertyConfigService;
  idVerifications: IdVerificationsService;

  constructor() {
    this.client = axios.create({
      baseURL: getApiHost()
    });

    this.responseInterceptor = this.client.interceptors.response;

    // interceptors
    this.client.interceptors.request.use(requestAuthInterceptor, undefined);
    this.client.interceptors.response.use(undefined, apiErrorHandler);

    // services
    this.reservation = new ReservationService(this.client);
    this.customer = new CustomerService(this.client);
    this.notification = new NotificationService(this.client);
    this.product = new ProductService(this.client);
    this.property = new PropertyService(this.client);
    this.propertyKeys = new PropertyKeysService(this.client);
    this.service = new ServiceService(this.client);
    this.propertyConfig = new PropertyConfigService(this.client);
    this.idVerifications = new IdVerificationsService(this.client);
  }
}

const apiClient = new ApiClient();

export { apiClient };
